import React from "react";
import { GrcCard } from "@/components/grc/shared";

const BAND_COLORS = {
  LOW: "bg-green-100 text-green-700 border-green-200",
  MED: "bg-amber-100 text-amber-700 border-amber-200",
  HIGH: "bg-red-100 text-red-700 border-red-200",
};

const GAP_BAR_COLORS = {
  PASS: "#388E3C",
  MINOR: "#FBC02D",
  MAJOR: "#F57C00",
  CRITICAL: "#D32F2F",
};

const SA_CODES = ["SA1", "SA2", "SA3", "SA4", "SA5", "SA6"];
const SB_CODES = ["SB1", "SB2", "SB3", "SB4", "SB5", "SB6", "SB7", "SB8"];

export const SDFDualModule = ({ data }) => {
  const ma = data?.module_a || {};
  const mb = data?.module_b || {};
  const heatmap = ma.factor_heatmap || {};
  const sbScores = mb.question_scores || {};
  const hasData = Object.keys(heatmap).length > 0 || Object.keys(sbScores).length > 0;

  return (
    <GrcCard
      title="SDF Dual Module"
      info="Module A estimates SDF designation probability from 6 risk factors. Module B measures obligation readiness across 8 questions."
      testId="sdf-dual-module-widget"
    >
      {!hasData ? (
        <div className="py-6 text-center text-xs text-slate-400" data-testid="sdf-no-data">
          No SDF module data in this assessment
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div data-testid="sdf-module-a-col">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-slate-500 uppercase">Module A</span>
              {ma.band && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${BAND_COLORS[ma.band] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {ma.band}
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {SA_CODES.map((code) => {
                const level = heatmap[code];
                return (
                  <div
                    key={code}
                    data-testid={`sdf-factor-${code}`}
                    className={`rounded-md border py-1.5 text-center ${BAND_COLORS[level] || "bg-slate-50 text-slate-400 border-slate-200"}`}
                    title={level ? `${code}: ${level}` : `${code}: no data`}
                  >
                    <div className="text-[10px] font-mono font-semibold">{code}</div>
                    <div className="text-[9px]">{level || "—"}</div>
                  </div>
                );
              })}
            </div>
            {ma.trigger_count > 0 && (
              <p className="mt-2 text-[10px] text-red-600 font-medium">
                {ma.trigger_count} trigger{ma.trigger_count > 1 ? "s" : ""}: {(ma.triggered_factors || []).join(", ")}
              </p>
            )}
          </div>

          <div data-testid="sdf-module-b-col">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-slate-500 uppercase">Module B</span>
              <span className="text-xs font-bold text-slate-800 tabular-nums">
                {mb.raw_score ?? "—"}/{mb.max_score || 24}
              </span>
            </div>
            {Object.keys(sbScores).length === 0 ? (
              <div className="text-[10px] text-slate-400 pt-2">{mb.status === "INACTIVE" ? "Module B not activated" : "No SB scores"}</div>
            ) : (
              <div className="space-y-1">
                {SB_CODES.filter((c) => sbScores[c]).map((code) => {
                  const s = sbScores[code];
                  const pct = ((s.score || 0) / 4) * 100;
                  return (
                    <div key={code} className="flex items-center gap-1.5" data-testid={`sdf-sb-bar-${code}`}>
                      <span className="w-7 text-[9px] font-mono text-slate-500">{code}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100">
                        <div
                          className="h-1.5 rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: GAP_BAR_COLORS[s.gap_tag] || "#94A3B8" }}
                        />
                      </div>
                      <span className="w-3 text-[9px] tabular-nums text-slate-500">{s.score ?? "—"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <p className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-400 leading-relaxed">
        Module A is designation probability (never generates gaps). Module B is obligation readiness.
      </p>
    </GrcCard>
  );
};
